import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import segmentsService from "../services/segmentsService";
import { Segment } from "../types/segment";
import { ReactComponent as InfoIcon } from "../assets/icons/Info.svg";

interface SubscriberSegmentsProps {
  subscriberId: string;
}

const SubscriberSegments: React.FC<SubscriberSegmentsProps> = ({ subscriberId }) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    loadSegments();
  }, []);

  const loadSegments = async () => {
    try {
      const data = await segmentsService.listSegments();
      setSegments(data);
    } catch (err) {
      console.error("Failed to load segments:", err);
    } finally {
      setLoaded(true);
    }
  };

  const handleAdd = async () => {
    if (!selectedSegmentId) return;
    setAdding(true);
    setError("");
    setSuccess("");
    try {
      await segmentsService.addSubscriber(selectedSegmentId, { subscriber_id: subscriberId });
      setSelectedSegmentId("");
      setSuccess("Added");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add to segment");
      setTimeout(() => setError(""), 3000);
    } finally {
      setAdding(false);
    }
  };

  const selectedSegment = segments.find((s) => s.id === selectedSegmentId);

  if (!loaded) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "20px",
        marginTop: "16px",
      }}
    >
      <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: 600 }}>Segments</h3>

      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <select
          value={selectedSegmentId}
          onChange={(e) => setSelectedSegmentId(e.target.value)}
          style={{
            flex: 1,
            padding: "6px 10px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "13px",
            backgroundColor: "#fff",
          }}
        >
          <option value="">Select a segment...</option>
          {segments.map((seg) => (
            <option key={seg.id} value={seg.id}>
              {seg.name} ({seg.segment_type === "static" ? "Static" : "Dynamic"})
            </option>
          ))}
        </select>
        <button
          onClick={handleAdd}
          disabled={adding || !selectedSegmentId}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "6px 12px",
            fontSize: "13px",
            fontWeight: 500,
            backgroundColor: "#000",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: adding || !selectedSegmentId ? "not-allowed" : "pointer",
            opacity: adding || !selectedSegmentId ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}
        >
          <Plus size={14} />
          {adding ? "Adding..." : "Add"}
        </button>
      </div>

      {selectedSegment?.segment_type === "dynamic" && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "8px",
            fontSize: "12px",
            color: "#92400e",
          }}
        >
          <div className="tooltip-container" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <InfoIcon style={{ width: "14px", height: "14px", opacity: 0.6, cursor: "help" }} />
            <div className="tooltip">
              If you manually add a subscriber to a dynamic segment, they could be removed on the next nightly recomputation if they no longer match the segment's rules.
            </div>
          </div>
          <span>Dynamic segment — manual additions may be overridden</span>
        </div>
      )}

      {success && (
        <div style={{ color: "#065f46", fontSize: "13px", marginTop: "8px", backgroundColor: "#d1fae5", padding: "6px 10px", borderRadius: "4px" }}>{success}</div>
      )}

      {error && (
        <div style={{ color: "red", fontSize: "13px", marginTop: "8px" }}>{error}</div>
      )}
    </div>
  );
};

export default SubscriberSegments;
